import React from 'react';
import type { MappingLevel } from '../../types/mappingDrillDown';
import { useMappingStackBreadcrumb } from '../../store/mappingStackStore';
import { isEvidenceOnlyMapping } from '../../config/mappingRules';

interface MappingBreadcrumbProps {
  className?: string;
  separator?: React.ReactNode;
}

export function MappingBreadcrumb({ className = '', separator = ' / ' }: MappingBreadcrumbProps) {
  const { stack, currentIndex, clearFromLevel } = useMappingStackBreadcrumb();

  if (stack.length === 0) {
    return null;
  }

  const items: React.ReactNode[] = [];
  stack.forEach((level: MappingLevel, index: number) => {
    if (index > 0) {
      items.push(
        <span key={`sep-${level.id}`} className="mx-1 text-gray-400" aria-hidden>
          {separator}
        </span>,
      );
    }
    const isCurrent = index === currentIndex;
    const mappingSuffix = (() => {
      if (!level.mappingContent) return 'results';
      return isEvidenceOnlyMapping(level.mappingContent.name) ? 'evidences' : 'results';
    })();
    const displayLabel =
      level.labelNode != null ? (
        <>
          {level.labelNode} {mappingSuffix}
        </>
      ) : level.mappingContent ? (
        `${level.label} ${mappingSuffix}`
      ) : (
        level.label
      );
    const ariaLabel = typeof level.label === 'string' ? level.label : mappingSuffix;
    if (isCurrent) {
      items.push(
        <span key={level.id} className="font-medium text-gray-900" aria-current="location">
          {displayLabel}
        </span>,
      );
    } else {
      items.push(
        <button
          key={level.id}
          type="button"
          onClick={() => clearFromLevel(index + 1)}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
          aria-label={`Back to ${ariaLabel}`}
        >
          {displayLabel}
        </button>,
      );
    }
  });

  return (
    <nav aria-label="Mapping navigation" className={`flex flex-wrap items-center text-sm ${className}`}>
      {items}
    </nav>
  );
}

export default MappingBreadcrumb;
